import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Image as ImageIcon, 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Upload,
  ChevronDown,
  ChevronRight,
  Layers,
  Home,
  Briefcase,
  ShoppingBag,
  FileText,
  Mail,
  Loader2,
  Link as LinkIcon,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Page and Section Configuration
const PAGE_CONFIG = {
  home: {
    name: 'Home',
    icon: Home,
    sections: [
      { id: 'hero', name: 'Hero Background', description: 'Main hero section background' },
      { id: 'founder', name: 'Founder Photo', description: 'Founder/CEO portrait image' },
      { id: 'gallery', name: 'Image Marquee', description: 'Scrolling image gallery' },
      { id: 'ventures', name: 'Ventures', description: 'Venture card images' },
    ]
  },
  about: {
    name: 'About',
    icon: User,
    sections: [
      { id: 'hero', name: 'Hero Image', description: 'Main about page hero' },
      { id: 'portrait', name: 'Portrait Photo', description: 'Full portrait image' },
      { id: 'gallery', name: 'Photo Gallery', description: 'About page gallery images' },
      { id: 'story', name: 'Story Section', description: 'Background images for story' },
    ]
  },
  evoke: {
    name: 'Approach / Services',
    icon: Briefcase,
    sections: [
      { id: 'hero', name: 'Hero Background', description: 'Services page hero' },
      { id: 'service-1', name: 'Service 1 Image', description: 'Brand Identity section' },
      { id: 'service-2', name: 'Service 2 Image', description: 'Launch Campaigns section' },
      { id: 'service-3', name: 'Service 3 Image', description: 'UGC Content section' },
      { id: 'gallery', name: 'Image Marquee', description: 'Scrolling image gallery' },
    ]
  },
  studio: {
    name: 'Studio / Shop',
    icon: ShoppingBag,
    sections: [
      { id: 'hero', name: 'Hero Background', description: 'Shop page hero' },
      { id: 'featured', name: 'Featured Products', description: 'Featured product images' },
    ]
  },
  blog: {
    name: 'Journal / Blog',
    icon: FileText,
    sections: [
      { id: 'hero', name: 'Hero Background', description: 'Blog page hero' },
      { id: 'featured', name: 'Featured Post', description: 'Featured article image' },
    ]
  },
  contact: {
    name: 'Contact',
    icon: Mail,
    sections: [
      { id: 'hero', name: 'Hero Background', description: 'Contact page hero' },
      { id: 'team', name: 'Team Photos', description: 'Team member photos' },
    ]
  },
  footer: {
    name: 'Footer',
    icon: Layers,
    sections: [
      { id: 'gallery', name: 'Footer Gallery', description: 'Footer image grid (4 images)' },
    ]
  }
};

const AdminImagesShowit = () => {
  const { token } = useAuth();
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedPages, setExpandedPages] = useState({ home: true });
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    title_es: '',
    alt_text: '',
    alt_text_es: '',
    url: '',
    category: 'page',
    page: '',
    section: '',
    display_order: 0,
    tags: '',
    focal_x: 50, // Percentage 0-100 (left to right)
    focal_y: 50  // Percentage 0-100 (top to bottom)
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Organize images by page and section
        const organized = {};
        data.forEach(img => {
          const page = img.page || 'uncategorized';
          const section = img.section || 'general';
          if (!organized[page]) organized[page] = {};
          if (!organized[page][section]) organized[page][section] = [];
          organized[page][section].push(img);
        });
        // Sort each section by display_order
        Object.keys(organized).forEach(page => {
          Object.keys(organized[page]).forEach(section => {
            organized[page][section].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
          });
        });
        setImages(organized);
      }
    } catch (error) {
      toast.error('Error loading images');
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, WebP, or SVG.');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert relative URL to absolute if needed
        let imageUrl = data.url;
        if (imageUrl.startsWith('/')) {
          imageUrl = `${API_URL}${imageUrl}`;
        }
        setFormData(prev => ({ 
          ...prev, 
          url: imageUrl,
          title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
          alt_text: prev.alt_text || file.name.replace(/\.[^/.]+$/, '')
        }));
        toast.success('Image uploaded!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error uploading image');
      }
    } catch (error) {
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      display_order: parseInt(formData.display_order) || 0,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
    };

    try {
      const url = editingImage 
        ? `${API_URL}/api/admin/images/${editingImage.id}`
        : `${API_URL}/api/admin/images`;
      
      const response = await fetch(url, {
        method: editingImage ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingImage ? 'Image updated!' : 'Image added!');
        setShowModal(false);
        resetForm();
        fetchImages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving image');
      }
    } catch (error) {
      toast.error('Error saving image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/images/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Image deleted!');
        fetchImages();
      } else {
        toast.error('Error deleting image');
      }
    } catch (error) {
      toast.error('Error deleting image');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title || '',
      title_es: image.title_es || '',
      alt_text: image.alt_text || '',
      alt_text_es: image.alt_text_es || '',
      url: image.url || '',
      category: image.category || 'page',
      page: image.page || '',
      section: image.section || '',
      display_order: image.display_order || 0,
      tags: (image.tags || []).join(', '),
      focal_x: image.focal_x ?? 50,
      focal_y: image.focal_y ?? 50
    });
    setShowModal(true);
  };

  const openAddModal = (pageId, sectionId) => {
    setSelectedPage(pageId);
    setSelectedSection(sectionId);
    resetForm();
    setFormData(prev => ({
      ...prev,
      page: pageId,
      section: sectionId,
      category: 'page'
    }));
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingImage(null);
    setFormData({
      title: '',
      title_es: '',
      alt_text: '',
      alt_text_es: '',
      url: '',
      category: 'page',
      page: selectedPage || '',
      section: selectedSection || '',
      display_order: 0,
      tags: '',
      focal_x: 50,
      focal_y: 50
    });
  };

  const togglePage = (pageId) => {
    setExpandedPages(prev => ({
      ...prev,
      [pageId]: !prev[pageId]
    }));
  };

  const getImagesForSection = (pageId, sectionId) => {
    return images[pageId]?.[sectionId] || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracota"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-images-showit">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Page Images</h1>
          <p className="text-stone-500">Manage images for each page and section</p>
        </div>
      </div>

      {/* Page Sections */}
      <div className="space-y-4">
        {Object.entries(PAGE_CONFIG).map(([pageId, pageConfig]) => {
          const PageIcon = pageConfig.icon;
          const isExpanded = expandedPages[pageId];
          
          return (
            <div 
              key={pageId}
              className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
              style={{ boxShadow: 'var(--shadow-panel)' }}
            >
              {/* Page Header */}
              <button
                onClick={() => togglePage(pageId)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-champagne flex items-center justify-center">
                    <PageIcon className="w-5 h-5 text-terracota" />
                  </div>
                  <div className="text-left">
                    <h2 className="font-semibold text-stone-900">{pageConfig.name}</h2>
                    <p className="text-xs text-stone-500">{pageConfig.sections.length} sections</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Count badge */}
                  <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs font-medium rounded-full">
                    {pageConfig.sections.reduce((acc, s) => acc + getImagesForSection(pageId, s.id).length, 0)} images
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-stone-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-stone-400" />
                  )}
                </div>
              </button>

              {/* Sections */}
              {isExpanded && (
                <div className="border-t border-stone-100">
                  {pageConfig.sections.map((section) => {
                    const sectionImages = getImagesForSection(pageId, section.id);
                    
                    return (
                      <div 
                        key={section.id}
                        className="px-6 py-4 border-b border-stone-100 last:border-b-0"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-stone-800">{section.name}</h3>
                            <p className="text-xs text-stone-400">{section.description}</p>
                          </div>
                          <button
                            onClick={() => openAddModal(pageId, section.id)}
                            className="flex items-center gap-1 text-xs font-medium text-terracota hover:text-stone-900 transition-colors"
                            data-testid={`add-image-${pageId}-${section.id}`}
                          >
                            <Plus className="w-4 h-4" />
                            Add Image
                          </button>
                        </div>

                        {/* Images Grid */}
                        {sectionImages.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {sectionImages.map((image) => (
                              <div 
                                key={image.id}
                                className="group relative aspect-square rounded-xl overflow-hidden bg-stone-100"
                              >
                                <img
                                  src={image.url}
                                  alt={image.alt_text}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==';
                                  }}
                                />
                                {/* Overlay on hover */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleEdit(image)}
                                    className="p-2 bg-white rounded-lg hover:bg-stone-100 transition-colors"
                                  >
                                    <Edit className="w-4 h-4 text-stone-700" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(image.id)}
                                    className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </button>
                                </div>
                                {/* Order badge */}
                                <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">
                                  #{image.display_order || 0}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-6 text-center border-2 border-dashed border-stone-200 rounded-xl">
                            <ImageIcon className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                            <p className="text-sm text-stone-400">No images yet</p>
                            <button
                              onClick={() => openAddModal(pageId, section.id)}
                              className="mt-2 text-xs font-medium text-terracota hover:underline"
                            >
                              Add your first image
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-stone-900">
                  {editingImage ? 'Edit Image' : 'Add Image'}
                </h2>
                {selectedPage && selectedSection && !editingImage && (
                  <p className="text-sm text-stone-500">
                    {PAGE_CONFIG[selectedPage]?.name} → {PAGE_CONFIG[selectedPage]?.sections.find(s => s.id === selectedSection)?.name}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Upload Mode Toggle */}
              <div className="flex rounded-xl bg-stone-100 p-1">
                <button
                  type="button"
                  onClick={() => setUploadMode('upload')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === 'upload' 
                      ? 'bg-white text-stone-900 shadow-sm' 
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === 'url' 
                      ? 'bg-white text-stone-900 shadow-sm' 
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <LinkIcon className="w-4 h-4" />
                  Paste URL
                </button>
              </div>

              {/* Upload Area or URL Input */}
              {uploadMode === 'upload' ? (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Upload Image
                  </label>
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      dragActive 
                        ? 'border-terracota bg-terracota/5' 
                        : 'border-stone-300 hover:border-terracota hover:bg-stone-50'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-10 h-10 text-terracota animate-spin mb-3" />
                        <p className="text-sm text-stone-500">Uploading...</p>
                      </div>
                    ) : formData.url ? (
                      <div className="space-y-3">
                        <div className="aspect-video max-w-[200px] mx-auto rounded-lg overflow-hidden bg-stone-100">
                          <img 
                            src={formData.url} 
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==';
                            }}
                          />
                        </div>
                        <p className="text-sm text-green-600 font-medium">✓ Image uploaded</p>
                        <p className="text-xs text-stone-400">Click or drop to replace</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="w-10 h-10 text-stone-400 mb-3" />
                        <p className="text-sm font-medium text-stone-700">
                          Drop your image here, or <span className="text-terracota">browse</span>
                        </p>
                        <p className="text-xs text-stone-400 mt-1">
                          JPG, PNG, GIF, WebP up to 10MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Image URL *
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="https://..."
                    required
                  />
                  {formData.url && (
                    <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-stone-100">
                      <img 
                        src={formData.url} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Title EN/ES */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Title (EN) *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Hero Image"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Title (ES)
                  </label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Imagen Principal"
                  />
                </div>
              </div>

              {/* Alt Text EN/ES */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Alt Text (EN) *
                  </label>
                  <input
                    type="text"
                    value={formData.alt_text}
                    onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Description for accessibility"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Alt Text (ES)
                  </label>
                  <input
                    type="text"
                    value={formData.alt_es}
                    onChange={(e) => setFormData({ ...formData, alt_text_es: e.target.value })}
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    placeholder="Descripción para accesibilidad"
                  />
                </div>
              </div>

              {/* Page & Section (if editing) */}
              {editingImage && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Page
                    </label>
                    <select
                      value={formData.page}
                      onChange={(e) => setFormData({ ...formData, page: e.target.value, section: '' })}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                    >
                      <option value="">Select page...</option>
                      {Object.entries(PAGE_CONFIG).map(([id, config]) => (
                        <option key={id} value={id}>{config.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Section
                    </label>
                    <select
                      value={formData.section}
                      onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                      disabled={!formData.page}
                    >
                      <option value="">Select section...</option>
                      {formData.page && PAGE_CONFIG[formData.page]?.sections.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-stone-400 mt-1">Lower numbers appear first</p>
              </div>

              {/* Drag to Position Image */}
              {formData.url && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Adjust Image Position
                  </label>
                  <p className="text-xs text-stone-400 mb-3">Drag the image to position it correctly</p>
                  
                  {/* Draggable Preview Container */}
                  <div 
                    className="relative w-full h-48 rounded-xl overflow-hidden bg-stone-100 border-2 border-dashed border-stone-300 cursor-move select-none"
                    onMouseDown={(e) => {
                      const container = e.currentTarget;
                      const rect = container.getBoundingClientRect();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startFocalX = formData.focal_x;
                      const startFocalY = formData.focal_y;
                      
                      const handleMouseMove = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        
                        // Invert: dragging right moves focal point left (lower %)
                        const newFocalX = Math.max(0, Math.min(100, startFocalX - (deltaX / rect.width) * 100));
                        const newFocalY = Math.max(0, Math.min(100, startFocalY - (deltaY / rect.height) * 100));
                        
                        setFormData(prev => ({
                          ...prev,
                          focal_x: Math.round(newFocalX),
                          focal_y: Math.round(newFocalY)
                        }));
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    <img 
                      src={formData.url} 
                      alt="Drag to position"
                      className="w-full h-full object-cover pointer-events-none"
                      style={{ 
                        objectPosition: `${formData.focal_x}% ${formData.focal_y}%`
                      }}
                      draggable={false}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCBmaWxsPSIjZGRkIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIvPjwvc3ZnPg==';
                      }}
                    />
                    {/* Drag indicator overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors">
                      <div className="bg-white/90 px-3 py-1.5 rounded-full text-xs font-medium text-stone-600 shadow-sm opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                        ↔ Drag to adjust
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick position buttons */}
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, focal_x: 50, focal_y: 0 })}
                      className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg border transition-all ${
                        formData.focal_y <= 20 ? 'border-terracota bg-terracota/10 text-terracota' : 'border-stone-200 text-stone-500 hover:border-stone-300'
                      }`}
                    >
                      Top
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, focal_x: 50, focal_y: 50 })}
                      className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg border transition-all ${
                        formData.focal_y > 20 && formData.focal_y < 80 ? 'border-terracota bg-terracota/10 text-terracota' : 'border-stone-200 text-stone-500 hover:border-stone-300'
                      }`}
                    >
                      Center
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, focal_x: 50, focal_y: 100 })}
                      className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg border transition-all ${
                        formData.focal_y >= 80 ? 'border-terracota bg-terracota/10 text-terracota' : 'border-stone-200 text-stone-500 hover:border-stone-300'
                      }`}
                    >
                      Bottom
                    </button>
                  </div>
                </div>
              )}

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-terracota/50"
                  placeholder="hero, featured, event"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-stone-300 rounded-xl text-stone-700 font-medium hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-terracota text-white rounded-xl font-medium hover:bg-stone-900 transition-colors"
                  data-testid="save-image-btn"
                >
                  {editingImage ? 'Update Image' : 'Add Image'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImagesShowit;
